/**
 * CyclePhaseControl
 *
 * Plain "Cycle phase" control for the cycle's Defaults (settings) tab —
 * restores a way to move a cycle's DEGAP phase without putting DEGAP on the
 * cycle's main page (canon glossary, "DEGAP in the App (Positioning Rule)":
 * DEGAP is orientation, not UI subject matter on primary/transactional
 * surfaces; it belongs in admin configuration). PR #712 mounted the
 * DEGAP-styled `DegapProcessPhaseIndicator` — "DEGAP Phase: DECIDE ENGAGE
 * GATHER ARRANGE PRIORITIZE" — always-on above the cycle's tab bar; the
 * owner rejected it, PR #732 unmounted it and round 8 deleted the file. This says "Current phase:
 * Gather" and "Move to Arrange" instead, and only appears in the Defaults
 * tab, not the cycle's main content.
 *
 * Shares its transition logic (the real signed-in user id, and surfacing
 * the RPC's blockers instead of swallowing them) with
 * DegapProcessPhaseIndicator via useCyclePhaseTransition.
 *
 * The caller is responsible for gating visibility to people who can
 * actually move the phase — see useCyclePhaseTransition's authority note.
 */
import { Loader2 } from 'lucide-react';
import { Panel, Callout } from '@/components/ds';
import { useCyclePhaseTransition } from '@/hooks/prioritization/useCyclePhaseTransition';
import type { DegapPhase } from '@/stores/prioritization/degapProcessStore';

interface CyclePhaseControlProps {
  cycleId: string;
}

/** "GATHER" → "Gather". Phase values are canon's uppercase constants; the
    settings surface reads them in sentence case, not as a shouted label. */
function phaseLabel(phase: DegapPhase): string {
  return phase.charAt(0) + phase.slice(1).toLowerCase();
}

export function CyclePhaseControl({ cycleId }: CyclePhaseControlProps) {
  const {
    currentPhase,
    nextPhase,
    previousPhase,
    canTransitionTo,
    pendingPhase,
    blockers,
    error,
    goToPhase,
  } = useCyclePhaseTransition(cycleId);

  if (!currentPhase) return null;

  const busy = pendingPhase !== null;

  return (
    <Panel label="Cycle phase">
      <p className="text-sm text-[var(--text-pri)]">
        Current phase: <span className="font-medium">{phaseLabel(currentPhase)}</span>
      </p>
      <p className="mt-1 text-xs text-[var(--text-sec)]">
        Moves forward one step at a time; you can always step back to an earlier phase.
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {previousPhase && (
          <button
            type="button"
            onClick={() => void goToPhase(previousPhase)}
            disabled={!canTransitionTo(previousPhase) || busy}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm border border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {pendingPhase === previousPhase && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Back to {phaseLabel(previousPhase)}
          </button>
        )}
        {nextPhase ? (
          <button
            type="button"
            onClick={() => void goToPhase(nextPhase)}
            disabled={!canTransitionTo(nextPhase) || busy}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm border border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-sec)] hover:bg-[var(--slate-dim)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {pendingPhase === nextPhase && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Move to {phaseLabel(nextPhase)}
          </button>
        ) : (
          <span className="text-xs text-[var(--text-ter)]">This is the last phase.</span>
        )}
      </div>

      {(blockers || error) && (
        <Callout tone="coral" className="mt-3">
          {error && <p>{error}</p>}
          {blockers && (
            <ul className="list-disc list-inside">
              {blockers.map((b, i) => <li key={i}>{b}</li>)}
            </ul>
          )}
        </Callout>
      )}
    </Panel>
  );
}
